import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    InputGroup,
    Nav,
    Tab,
    Tabs,
    Modal
} from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import Header from '../../Header/Header.tsx';
import Footer from '../../Footer/Footer.tsx';
import Amico from "../../../../images/amico.png"
import CheckMark from "../../../../images/check-mark-icon.svg"
import { FormLabel, FormControl } from 'react-bootstrap';
import SecureIcon from "../../../../images/secure-icon.svg"
import apiConfig from '../../config/apiConfig.js';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../utils/constants';
import { toast } from 'react-toastify';
import GiftLoader from '../../../common/GiftLoader';
import SuccessIcon from '../../../../images/success-new.png';
import "./Subscription.scss";

// Initialize Stripe Promise
const stripePromise = loadStripe((import.meta as any).env.VITE_STRIPE_PUBLISHABLE_KEY!);

interface SubscriptionPlan {
    name: string;
    description: string;
    priceMonthly: number;
    priceYearly: number;
    stripePriceIdMonthly: string;
    stripePriceIdYearly: string;
    features: string[];
}
interface FormErrors {
    cardNumber: string;
    cardHolder: string;
    cardExpiry: string;
    cardCvc: string;
    promoCode: string;
}

const SubscriptionForm: React.FC = () => {
    const navigate = useNavigate();
    const [legacyContact, setLegacyContact] = useState<boolean>(false);
    const stripe = useStripe();
    const elements = useElements();
    const [activeTab, setActiveTab] = useState<string>('monthly');
    const [paymentMethod, setPaymentMethod] = useState<string>('credit');
    const [promoCode, setPromoCode] = useState<string>('');
    const [promoApplied, setPromoApplied] = useState<boolean>(false);
    const [validatedPromoCode, setValidatedPromoCode] = useState<string>('');
    const [discountInfo, setDiscountInfo] = useState<{
        discountType: string;
        discountAmount: number;
        promoId: string;
        couponId: string;
        multipleUse: boolean;
    } | null>(null);
    const [cardHolder, setCardHolder] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [promoLoading, setPromoLoading] = useState<boolean>(false);
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null);
    const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
    const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

    // Form errors state
    const [formErrors, setFormErrors] = useState<FormErrors>({
        cardNumber: '',
        cardHolder: '',
        cardExpiry: '',
        cardCvc: '',
        promoCode: ''
    });

    // Refs for auto-focus
    const cardHolderRef = useRef<HTMLInputElement>(null);

 // Check if user already has active subscription
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          // No token, user can proceed to subscription
          return;
        }

        const response = await axios.get(`${apiConfig.apiBaseURL}/profile`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (response.data && response.data.accountStatus === 'Active') {
          // User already has active subscription, redirect them
          toast.info('You already have an active subscription!');
          navigate('/my-gifts');
        } else if (response.data) {
          // Check if user has already completed legacy contacts
          // If primaryLegacyContactId exists, they've already set up legacy contacts
          const hasLegacyContact = !!(response.data.primaryLegacyContactId || response.data.primaryLegacyContact);
          setLegacyContact(hasLegacyContact);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        // If error, allow user to continue to subscription page
      }
    };

    checkSubscriptionStatus();
  }, [navigate]);

  useEffect(() => {
    const fetchSubscriptionPlan = async () => {
      try {
        const response = await axios.get(`${apiConfig.apiBaseURL}${API_ENDPOINTS.GET_SUBSCRIPTION_PLANS}`);
        if (response.data.status === "Success" && response.data.data.length > 0) {
          setSubscriptionPlan(response.data.data[0]); // Since only one plan is returned
        } else {
          toast.error("Subscription plans not found");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load subscription plan");
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchSubscriptionPlan();
  }, []);

    const PRICE_IDS = useMemo(() => ({
        monthly: subscriptionPlan?.stripePriceIdMonthly || '',
        yearly: subscriptionPlan?.stripePriceIdYearly || ''
    }), [subscriptionPlan]);

    // Auto-focus on first input field
    useEffect(() => {
        if (cardHolderRef.current) {
            cardHolderRef.current.focus();
        }
    }, []);

    // Clear Stripe element errors on change
    useEffect(() => {
        if (elements) {
            const cardNumber = elements.getElement(CardNumberElement);
            const cardExpiry = elements.getElement(CardExpiryElement);
            const cardCvc = elements.getElement(CardCvcElement);

            if (cardNumber) {
                cardNumber.on('change', () => {
                    setFormErrors(prev => ({ ...prev, cardNumber: '' }));
                });
            }

            if (cardExpiry) {
                cardExpiry.on('change', () => {
                    setFormErrors(prev => ({ ...prev, cardExpiry: '' }));
                });
            }

            if (cardCvc) {
                cardCvc.on('change', () => {
                    setFormErrors(prev => ({ ...prev, cardCvc: '' }));
                });
            }

            // Cleanup
            return () => {
                if (cardNumber) cardNumber.off('change');
                if (cardExpiry) cardExpiry.off('change');
                if (cardCvc) cardCvc.off('change');
            };
        }
    }, [elements]);

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Comprehensive form validation
    const validateForm = (): boolean => {
        const errors: any = {
            cardNumber: '',
            cardHolder: '',
            cardExpiry: '',
            cardCvc: '',
            promoCode: ''
        };

        let isValid = true;

        // Validate card holder name
        if (!cardHolder.trim()) {
            errors.cardHolder = 'Cardholder name is required';
            isValid = false;
        }

        // Validate Stripe elements
        if (!elements) {
            toast.error('Payment form not loaded properly. Please refresh the page.');
            return false;
        }

        const cardNumber = elements.getElement(CardNumberElement);
        const cardExpiry = elements.getElement(CardExpiryElement);
        const cardCvc = elements.getElement(CardCvcElement);

        // Check if Stripe elements exist and are empty (using _empty property check)
        if (!cardNumber || (cardNumber as any)._empty) {
            errors.cardNumber = 'Card number is required';
            isValid = false;
        }

        if (!cardExpiry || (cardExpiry as any)._empty) {
            errors.cardExpiry = 'Expiry date is required';
            isValid = false;
        }

        if (!cardCvc || (cardCvc as any)._empty) {
            errors.cardCvc = 'CVC is required';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const applyPromoCode = async () => {
        if (!promoCode.trim()) {
            toast.error('Please enter a promo code');
            return;
        }

        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            toast.error('Please login to apply promo code');
            return;
        }

        setPromoLoading(true);

        try {
            const response = await axios.post(
                `${apiConfig.apiAdminBaseURL}/promo/validate-promo-code`,
                { code: promoCode.trim() },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`
                    }
                }
            );

            if (response.data.status === 'Success') {
                setPromoApplied(true);
                setValidatedPromoCode(promoCode.trim());
                setDiscountInfo({
                    discountType: response.data.discountType,
                    discountAmount: response.data.discountAmount,
                    promoId: response.data.promoId,
                    couponId: response.data.couponId,
                    multipleUse: response.data.multipleUse
                });
                setFormErrors(prev => ({ ...prev, promoCode: '' }));
                toast.success('Promo code applied successfully');
            } else {
                toast.error(response.data.message || 'Invalid promo code');
                setFormErrors(prev => ({ ...prev, promoCode: response.data.message || 'Invalid promo code' }));
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Failed to validate promo code';
            toast.error(errorMessage);
            setFormErrors(prev => ({ ...prev, promoCode: errorMessage }));
        } finally {
            setPromoLoading(false);
        }
    };

    const removePromoCode = () => {
        setPromoCode('');
        setPromoApplied(false);
        setValidatedPromoCode('');
        setDiscountInfo(null);
        setFormErrors(prev => ({ ...prev, promoCode: '' }));
    };

const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    
    if (!stripe || !elements) {
        toast.error('Payment system not ready. Please refresh the page.');
        return;
    }

    if (!validateForm()) {
        // Validation errors are already shown inline below the fields
        // No need for a toast message here
        return;
    }

    setLoading(true);
    setHasSubmitted(true);

    try {
      // Create payment method
      const { paymentMethod, error: paymentError } =
        await createStripePaymentMethod();
      if (paymentError) {
        toast.error(paymentError);
        return;
      }

      // Create subscription
      const subscriptionResponse = await createSubscriptionIntent(
        paymentMethod.id
      );
      if (!subscriptionResponse.success) {
        toast.error(subscriptionResponse.error);
        return;
      }

      // Confirm payment
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(subscriptionResponse.clientSecret, {
          payment_method: paymentMethod.id,
        });

      if (confirmError) {
        toast.error(confirmError.message || "Payment confirmation failed");
        return;
      }

      // Handle success
      if (paymentIntent?.status === "succeeded") {
        // Refetch profile to get updated legacyContact status
        try {
          const authToken = localStorage.getItem('authToken');
          const profileResponse = await axios.get(`${apiConfig.apiBaseURL}/profile`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          });

          // Update state with fresh data from backend
          if (profileResponse.data) {
            // Check if primaryLegacyContactId exists to determine if user has completed legacy contact setup
            const updatedLegacyContact = !!(profileResponse.data.primaryLegacyContactId || profileResponse.data.primaryLegacyContact);
            setLegacyContact(updatedLegacyContact);
            localStorage.setItem("accountStatus", "Active");
            localStorage.setItem("legacyContact", updatedLegacyContact.toString());
          }
        } catch (error) {
          console.error('Error fetching profile after payment:', error);
          // Still proceed with success modal even if profile fetch fails
          localStorage.setItem("accountStatus", "Active");
        }

        toast.success("Payment successful! Welcome to your premium account!");

        // Show success modal instead of redirecting
        setShowSuccessModal(true);

        // Trigger profile update event so Header can refresh
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        toast.error("Payment was not completed successfully");
      }
    } catch (error: any) {
        handlePaymentError(error);
    } finally {
        setLoading(false);
    }
};


const createStripePaymentMethod = async (): Promise<
  | { paymentMethod: any; error?: never }
  | { error: string; paymentMethod?: never }
> => {
  const cardElement = elements!.getElement(CardNumberElement);

  if (!cardElement) {
    return { error: "Card information not found" };
  }

  const { error, paymentMethod } = await stripe!.createPaymentMethod({
    type: "card",
    card: cardElement,
    billing_details: { name: cardHolder.trim() },
  });

  if (error) {
    return { error: error.message || "Payment method creation failed" };
  }

  return { paymentMethod };
};


const createSubscriptionIntent = async (paymentMethodId: string) => {
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
        return { success: false, error: 'Please login to continue' };
    }

    const subscriptionData: any = {
        priceId: PRICE_IDS[activeTab as keyof typeof PRICE_IDS],
        paymentMethodId
    };

    // Add couponId if promo code is applied
    if (promoApplied && discountInfo?.couponId) {
        subscriptionData.couponId = discountInfo.couponId;
    }

    try {
        const response = await axios.post(
            `${apiConfig.apiBaseURL}${API_ENDPOINTS.CREATE_SUBSCRIPTION}`,
            subscriptionData,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                }
            }
        );

        if (response.data.status === 'Success' && response.data.clientSecret) {
            return {
                success: true,
                clientSecret: response.data.clientSecret,
                data: response.data
            };
        } else {
            return {
                success: false,
                error: response.data.message || 'Subscription creation failed'
            };
        }

    } catch (error: any) {
        return {
            success: false,
            error: error.response?.data?.message || 'Failed to create subscription'
        };
    }
};

const handlePaymentError = (error: any) => {

    const backendMessage = error.response?.data?.message;

    if (backendMessage) {
        toast.error(backendMessage);
    } else {
        toast.error('Payment failed. Please try again.');
    }
};


    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

   const handleCardHolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    value = value.replace(/^\s+/, '');                // Trim leading spaces
    value = value.replace(/\s{2,}/g, ' ');            // Replace multiple spaces with single space
    value = value.replace(/[^a-zA-Z\s]/g, '');        // Only allow letters and spaces

    setCardHolder(value);

    if (hasSubmitted && formErrors.cardHolder) {
        setFormErrors(prev => ({ ...prev, cardHolder: '' }));
    }
};


    const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPromoCode(e.target.value.toUpperCase());
        if (hasSubmitted && formErrors.promoCode) {
            setFormErrors(prev => ({ ...prev, promoCode: '' }));
        }
        // Reset promo applied state when user changes the code
        if (promoApplied) {
            setPromoApplied(false);
            setValidatedPromoCode('');
            setDiscountInfo(null);
        }
    };

    // Stripe element options to match original UI
    const stripeElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: '#495057',
                fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                '::placeholder': {
                    color: '#6c757d',
                },
            },
            invalid: {
                color: '#dc3545',
            },
        },
    };

    return (
      <>
        <Header activeTab={undefined} toggle={undefined} />
        <section className="subscription-sec" style={{ background: "#EFF4FF" }}>
          <div className="p-0 container">
            <Row className="justify-content-center subscription-sec-in">
              {/* Left Column - Plan Details */}
              <Col lg={6} md={12} className="subscription-left-block">
                <Card className="h-100">
                  <Card.Body className="p-2 subscription-grid">
                    <h3 className="text-primary fw-bold mb-4">
                      Subscription Plan
                    </h3>

                    {loadingPlans ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                        <GiftLoader 
                          text="Loading subscription plans..."
                          size="medium"
                          showText={true}
                        />
                      </div>
                    ) : (
                      <>
                        {/* Custom Tab Navigation */}
                        <Nav variant="pills" className="mb-0">
                          <Nav.Item>
                            <Nav.Link 
                              active={activeTab === 'monthly'}
                              onClick={() => setActiveTab('monthly')}
                            >
                              Monthly
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link 
                              active={activeTab === 'yearly'}
                              onClick={() => setActiveTab('yearly')}
                            >
                              Yearly
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>

                        {/* Tab Content */}
                        <div className="tab-content">
                          {/* Monthly Tab Pane */}
                          <div className={`tab-pane ${activeTab === 'monthly' ? 'active show' : ''}`}>
                            <Card
                              className="mb-4 border-0 box-card-block"
                              style={{ background: "#3957A0", color: "white", borderRadius: "16px" }}
                            >
                              <Card.Body className="p-2 text-left position-relative">
                                <div className='d-flex justify-content-between '>
                                  <div className='my-auto ps-3'>
                                  
                                  <h1 className="display-2 fw-bold mb-0">
                                    ${subscriptionPlan?.priceMonthly || "99"}
                                  </h1>
                                
                                <p className="mb-0 fs-5 opacity-90">
                                  All-in-one package
                                </p>
                                  </div>

                                <div>
                                <img src={Amico} alt="" width={150}/>
                                </div>

                                </div>
                                
                                
                              </Card.Body>
                            </Card>
                            <div>
                              <h5 className="mb-3 fw-bold" style={{fontSize:'18px', color:"#677289"}}>What's included</h5>
                              <ul className="list-unstyled m-0 p-0">
                                {subscriptionPlan?.features?.map(
                                   (feature: string, index: number) => (
                                    <li
                                      key={index}
                                      className="d-flex align-items-center mb-3"
                                    >
                                      <div className="me-3">
                                        <img src={CheckMark} alt="" style={{ width: "20px", height: "20px" }} />
                                      </div>
                                      <span className="fw-bold" style={{color:"#3957A0", fontSize:"16px"}}>
                                        {feature}
                                      </span>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </div>

                          {/* Yearly Tab Pane */}
                          <div className={`tab-pane ${activeTab === 'yearly' ? 'active show' : ''}`}>
                            <Card
                              className="mb-4 border-0 box-card-block"
                              style={{ background: "#3957A0", color: "white", borderRadius: "16px" }}
                            >
                              <Card.Body className="p-2 text-left position-relative">
                                <div className='d-flex justify-content-between '>
                                <div className='my-auto ps-3'>
                                  
                                  <h1 className="display-2 fw-bold mb-0">
                                    ${subscriptionPlan?.priceYearly || "999"}
                                  </h1>
                                
                                <p className="mb-0 fs-5 opacity-90">
                                  All-in-one package
                                </p>
                                </div>
                                <div>
                                   
                                  <img src={Amico} alt="" width={150}/>
                                
                                </div>
                                </div>
                                
                               
                              </Card.Body>
                            </Card>
                            <div>
                              <h5 className="mb-3 fw-bold" style={{fontSize:'18px', color:"#677289"}}>What's included</h5>
                              <ul className="list-unstyled m-0 p-0">
                                {subscriptionPlan?.features?.map(
                                   (feature: string, index: number) => (
                                    <li
                                      key={index}
                                      className="d-flex align-items-center mb-3"
                                    >
                                      <div className="me-3">
                                        <img src={CheckMark} alt="" style={{ width: "20px", height: "20px" }} />
                                      </div>
                                      <span className="fw-bold" style={{color:"#3957A0", fontSize:"16px"}}>
                                        {feature}
                                      </span>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Column - Payment Details */}
              <Col lg={6} md={12} className="subscription-right-block">
                <Card className="shadow-sm h-100 subscription-grid">
                  <Card.Body className="p-4 subscription-grid">
                    <h3 className="text-primary fw-bold mb-4">
                      Complete Your Subscription
                    </h3>

                    {/* Promo Code Section */}
                    <div className="p-3 rounded mb-4 promo-code-sec">
                      <FormLabel className="text-white mb-2">
                        Have a Promo Code?
                      </FormLabel>
                      <InputGroup>
                        <FormControl
                          type="text"
                          value={promoCode}
                          onChange={handlePromoCodeChange}
                          onKeyDown={handleKeyDown}
                          className="bg-white"
                          placeholder="Enter promo code"
                          disabled={loading || promoLoading}
                          isInvalid={!!formErrors.promoCode}

                                                  />
                        <Button
                          onClick={promoApplied && promoCode === validatedPromoCode ? removePromoCode : applyPromoCode}
                          className="main-bttn w-auto btn btn-primary"
                          disabled={
                            loading || promoLoading || !promoCode.trim()
                          }
                        >
                          {promoLoading
                            ? "Verifying..."
                            : promoApplied && promoCode === validatedPromoCode
                              ? "Remove"
                              : "Apply"}
                        </Button>
                      </InputGroup>
                      {formErrors.promoCode && (
                        <small
                          style={{
                            color: "#ffffff",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          {formErrors.promoCode}
                        </small>
                      )}
                      {promoApplied && promoCode === validatedPromoCode && (
                        <small className="d-block mt-2 opacity-75 text-white">
                          ✓ Promo code "{validatedPromoCode}" applied successfully
                        </small>
                      )}
                      {promoLoading && (
                        <small className="d-block mt-2 opacity-75 text-white">
                          Verifying promo code...
                        </small>
                      )}
                    </div>

                    {/* Payment Form */}
                    <Form onSubmit={handleSubmit}>
                      {paymentMethod === "credit" && (
                        <>
                          {/* Card Holder Name */}
                          <Form.Group className="form-group">
                            <FormLabel>Card Holder Name *</FormLabel>
                            <FormControl
                              type="text"
                              value={cardHolder}
                              onChange={handleCardHolderChange}
                              onKeyDown={handleKeyDown}
                              placeholder="Card Holder Name"
                              disabled={loading}
                              className={`p-3`}
                            />
                            {formErrors.cardHolder && (
                              <small
                                style={{
                                  color: "#dc3545",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {formErrors.cardHolder}
                              </small>
                            )}
                          </Form.Group>

                          {/* Card Number */}
                          <Form.Group className="form-group">
                            <FormLabel>Card Number *</FormLabel>
                            <div className={`form-control p-3`}>
                              <CardNumberElement
                                options={{
                                  ...stripeElementOptions,
                                  showIcon: true,
                                  iconStyle: "solid",
                                  disableLink: true,
                                }}
                              />
                            </div>
                            {formErrors.cardNumber && (
                              <small
                                style={{
                                  color: "#dc3545",
                                  display: "block",
                                  marginTop: "4px",
                                }}
                              >
                                {formErrors.cardNumber}
                              </small>
                            )}
                          </Form.Group>

                          <Row>
                            <Col>
                              {/* Expiry Date */}
                              <Form.Group className="form-group">
                                <FormLabel>Expiry Date *</FormLabel>
                                <div className={`form-control `}>
                                  <CardExpiryElement
                                    options={stripeElementOptions}
                                  />
                                </div>
                                {formErrors.cardExpiry && (
                                  <small
                                    style={{
                                      color: "#dc3545",
                                      display: "block",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {formErrors.cardExpiry}
                                  </small>
                                )}
                              </Form.Group>
                            </Col>
                            <Col>
                              {/* CVV */}
                              <Form.Group className="form-group">
                                <FormLabel>CVC *</FormLabel>
                                <div className={`form-control`}>
                                  <CardCvcElement
                                    options={stripeElementOptions}
                                  />
                                </div>
                                {formErrors.cardCvc && (
                                  <small
                                    style={{
                                      color: "#dc3545",
                                      display: "block",
                                      marginTop: "4px",
                                    }}
                                  >
                                    {formErrors.cardCvc}
                                  </small>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}

                    </Form>

                    {/* Total Section */}
                    <div className="border-top pt-3 mt-2">
                      <h5 className="text-primary mb-3">Total</h5>

                      <div className="d-flex justify-content-between mb-2">
                        <span>
                          <span className="text-muted">
                            ({activeTab})
                          </span>
                        </span>
                        <span className="fw-bold" style={{ color: "#3957A0" }}>
                          $
                          {activeTab === "monthly"
                            ? subscriptionPlan?.priceMonthly || "--"
                            : subscriptionPlan?.priceYearly || "--"}
                        </span>
                      </div>

                      {/* Discount Section */}
                      {promoApplied && discountInfo && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-success">
                            Discount ({discountInfo.discountType === 'percentage' ? `${discountInfo.discountAmount}%` : `$${discountInfo.discountAmount}`})
                          </span>
                          <span className="fw-bold text-success">
                            -$
                            {discountInfo.discountType === 'percentage'
                              ? ((activeTab === "monthly"
                                  ? subscriptionPlan?.priceMonthly || 0
                                  : subscriptionPlan?.priceYearly || 0) * discountInfo.discountAmount / 100).toFixed(2)
                              : discountInfo.discountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}

                      <hr />
                                        <div className="d-flex justify-content-between mb-3">
                                            <h5 className="text-primary fw-bold">Grand Total</h5>
                                            <h5 className="text-primary fw-bold">
                                                $
                                                {(() => {
                                                    const basePrice = activeTab === "monthly"
                                                        ? subscriptionPlan?.priceMonthly || 0
                                                        : subscriptionPlan?.priceYearly || 0;

                                                    if (promoApplied && discountInfo) {
                                                        const discount = discountInfo.discountType === 'percentage'
                                                            ? (basePrice * discountInfo.discountAmount / 100)
                                                            : discountInfo.discountAmount;
                                                        return (basePrice - discount).toFixed(2);
                                                    }

                                                    return basePrice;
                                                })()}
                                            </h5>
                                        </div>
                                        <Button
                                            size="lg"
                                            className="main-bttn w-100 btn btn-primary mb-3"
                                            onClick={() => handleSubmit()}
                                            disabled={loading || !stripe}
                                        >
                                            {loading ? "Processing..." : "Complete Purchase"}
                                        </Button>

                      <div className="text-left secure-text">
                        <small className="text-muted">
                          <img src={SecureIcon} alt="" />
                          <span>Secure payment</span>
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* Success Modal */}
        <Modal
          show={showSuccessModal}
          onHide={() => {}}
          centered
          backdrop="static"
          keyboard={false}
          className="success-modal "
        >
          <Modal.Header className="border-0 pb-0 pt-5 px-5">
            <Modal.Title className="w-100 text-center">
              <h2 style={{color:'#3957A0', fontWeight:700}}>Success!</h2>
              <p style={{color:'#677289', fontSize:'20px', fontWeight:700}}>Your subscription payment has been processed successfully.</p>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center pt-0 pb-5 px-5">
            <img src={SuccessIcon} alt="logo"/>
            
            <p style={{color:'#677289', fontSize:'20px', fontWeight:700}}>You can now begin choosing gifts for your loved ones.</p>
            <Button
              className="main-bttn btn btn-primary w-auto"
              size="lg"
              onClick={() => {
                setShowSuccessModal(false);
                // Check if user already completed legacy contact setup
                // If yes, go directly to dashboard; if no, go to legacy contact setup
                navigate(legacyContact ? "/my-gifts" : "/legacy-contact");
              }}
              style={{ minWidth: "200px" }}
            >
              Get Started
            </Button>
          </Modal.Body>
        </Modal>

        <Footer />
      </>
    );
};

// Main Subscription component wrapped with Elements provider
const Subscription: React.FC = () => {
    return (
        <Elements stripe={stripePromise}>
            <SubscriptionForm />
        </Elements>
    );
};

export default Subscription;